export function singleSportGameKey(row) {
  const team = String(row?.Team || row?.["Team Name"] || "").trim();
  const opponent = String(row?.Opponent || row?.["Opponent Name"] || "").trim();
  if (!team || !opponent || /^(?:nan|null|undefined)$/i.test(team) || /^(?:nan|null|undefined)$/i.test(opponent)) return "";
  return [team, opponent].sort().join(" vs ");
}

export function canonicalTeamSportFixtureKey(row) {
  const sport = clean(row?.Sport || row?.League).toUpperCase();
  const gameId = clean(row?.["Game ID"] || row?.GameID || row?.game_id);
  if (gameId && sport === "WNBA") return `wnba:id:${gameId}`;
  if (sport === "MLB") return canonicalMLBGameKey(row);
  return singleSportGameKey(row);
}

export function canonicalMLBGameKey(row) {
  const gamePk = clean(row?.["MLB gamePk"] || row?.["MLB GamePk"] || row?.gamePk || row?.game_pk);
  const gameId = clean(row?.["Game ID"] || row?.GameID || row?.game_id);
  if (gamePk) return `mlb:gamepk:${gamePk}`;
  if (gameId) return `mlb:game:${gameId}`;
  return "";
}

export function canonicalGameSelectionKey(row, sportOverride = "") {
  const sport = clean(sportOverride || row?.Sport || row?.League).toUpperCase();
  if (sport === "MLB") return canonicalMLBGameKey(row);
  if (sport === "SOCCER") return canonicalSoccerFixtureKey(row);
  if (sport === "NFL") return canonicalNFLFixtureKey(row);
  if (sport === "CFB") return canonicalCFBFixtureKey(row);
  if (sport === "WNBA") return canonicalTeamSportFixtureKey({ ...row, Sport: "WNBA" });
  const id = clean(row?.["Game ID"] || row?.["Fixture ID"] || row?.GameID || row?.FixtureID || row?.game_id);
  return id ? `${sport.toLowerCase()}:id:${id}` : singleSportGameKey(row);
}

export function projectionCountsByGame(rows, sportOverride = "") {
  const identities = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const key = canonicalGameSelectionKey(row, sportOverride);
    if (!key) return;
    const projectionId = clean(row?.["Projection ID"] || row?.ProjectionID);
    const identity = projectionId || `row:${index}`;
    if (!identities.has(key)) identities.set(key, new Set());
    identities.get(key).add(identity);
  });
  return new Map([...identities].map(([key, values]) => [key, values.size]));
}

export function buildGenericGameOptions(rows, sportOverride = "") {
  const games = new Map();
  const counts = projectionCountsByGame(rows, sportOverride);
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = canonicalGameSelectionKey(row, sportOverride);
    if (!key || games.has(key)) continue;
    const { home, away } = mlbSides(row);
    games.set(key, {
      key,
      display: home && away ? `${away} @ ${home}` : singleSportGameKey(row),
      representativeRow: row,
      projectionCount: counts.get(key) || 0,
    });
  }
  return [...games.values()].sort((a, b) => {
    const left = new Date(fixtureStart(a.representativeRow)).getTime();
    const right = new Date(fixtureStart(b.representativeRow)).getTime();
    return (Number.isFinite(left) ? left : Number.MAX_SAFE_INTEGER) - (Number.isFinite(right) ? right : Number.MAX_SAFE_INTEGER)
      || a.display.localeCompare(b.display);
  });
}

const MLB_TIME_WINDOWS = ["Early", "Afternoon", "Evening", "Late"];

function localDateTime(timestamp, timeZone) {
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    ...(timeZone ? { timeZone } : {}),
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const values = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  const hour = Number(values.hour) % 24;
  return {
    date,
    localDate: `${values.year}-${values.month}-${values.day}`,
    hour,
    window: hour < 12 ? "Early" : hour < 17 ? "Afternoon" : hour < 21 ? "Evening" : "Late",
  };
}

function mlbSides(row) {
  const team = clean(row?.Team || row?.["Team Name"]);
  const opponent = clean(row?.Opponent || row?.["Opponent Name"]);
  const sourceHome = clean(row?.["Home Team"] || row?.HomeTeam || row?.home_team);
  const sourceAway = clean(row?.["Away Team"] || row?.AwayTeam || row?.away_team);
  if (sourceHome && sourceAway) return { home: sourceHome, away: sourceAway };
  const homeAway = clean(row?.["Home/Away"] || row?.HomeAway || row?.home_away).toUpperCase();
  if (homeAway === "HOME") return { home: team, away: opponent };
  if (homeAway === "AWAY") return { home: opponent, away: team };
  return { home: "", away: "" };
}

/** Build MLB drawer groups without using matchup text as selection identity. */
export function buildMLBGameGroups(rows, { timeZone } = {}) {
  const games = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = canonicalMLBGameKey(row);
    if (!key) continue;
    const timestamp = fixtureStart(row);
    const timing = localDateTime(timestamp, timeZone);
    const { home, away } = mlbSides(row);
    if (!timing || !home || !away) continue;
    const current = games.get(key) || {
      key, home, away, canonicalStartTime: timestamp, startMs: timing.date.getTime(),
      localDate: timing.localDate, window: timing.window, representativeRow: row,
      projectionIds: new Set(), explicitGameNumber: null, projectionCountOverride: null,
    };
    const projectionId = clean(row?.["Projection ID"] || row?.ProjectionID);
    if (projectionId) current.projectionIds.add(projectionId);
    const countOverride = Number(row?.__projectionCount);
    if (Number.isFinite(countOverride) && countOverride >= 0) current.projectionCountOverride = countOverride;
    const gameNumber = Number(row?.["Doubleheader Game Number"] || row?.["Game Number"] || row?.game_number);
    if (gameNumber === 1 || gameNumber === 2) current.explicitGameNumber = gameNumber;
    games.set(key, current);
  }

  const matchups = new Map();
  for (const game of games.values()) {
    const parentKey = `mlb:matchup:${game.away}|${game.home}|${game.localDate}`;
    const parent = matchups.get(parentKey) || { key: parentKey, display: `${game.away} @ ${game.home}`, games: [] };
    parent.games.push(game);
    matchups.set(parentKey, parent);
  }

  const parents = [...matchups.values()].map((parent) => {
    parent.games.sort((a, b) => a.startMs - b.startMs || a.key.localeCompare(b.key));
    const isDoubleheader = parent.games.length > 1;
    parent.games = parent.games.map((game, index) => ({
      ...game,
      projectionCount: game.projectionCountOverride ?? game.projectionIds.size,
      gameNumber: isDoubleheader ? (game.explicitGameNumber || index + 1) : null,
    }));
    parent.childKeys = parent.games.map((game) => game.key);
    parent.projectionCount = parent.games.reduce((total, game) => total + game.projectionCount, 0);
    parent.isDoubleheader = isDoubleheader;
    parent.startMs = parent.games[0].startMs;
    parent.window = parent.games[0].window;
    return parent;
  });

  const doubleheaders = parents.filter((parent) => parent.isDoubleheader)
    .sort((a, b) => a.startMs - b.startMs || a.display.localeCompare(b.display));
  const windows = MLB_TIME_WINDOWS.map((window) => ({
    window,
    matchups: parents.filter((parent) => !parent.isDoubleheader && parent.window === window)
      .sort((a, b) => a.startMs - b.startMs || a.display.localeCompare(b.display)),
  })).filter((group) => group.matchups.length);
  return [
    ...(doubleheaders.length ? [{ window: "Doubleheaders", isDoubleheaders: true, matchups: doubleheaders }] : []),
    ...windows,
  ];
}

export function toggleAuthoritativeGameSelection(currentKeys, targetKeys) {
  const current = Array.isArray(currentKeys) ? currentKeys : [];
  const targets = [...new Set((Array.isArray(targetKeys) ? targetKeys : []).filter(Boolean))];
  const allSelected = targets.length > 0 && targets.every((key) => current.includes(key));
  return allSelected
    ? current.filter((key) => !targets.includes(key))
    : [...new Set([...current, ...targets])];
}

export function toggleCollapsedFilter(current, key) {
  return { ...(current || {}), [key]: current?.[key] !== true };
}

export function labeledCount(count, singular, plural = `${singular}s`) {
  const value = Number.isFinite(Number(count)) ? Number(count) : 0;
  return `${value} ${value === 1 ? singular : plural}`;
}

export function mlbPropCountLabel(count) {
  return Number(count) > 0 ? labeledCount(count, "prop") : "No props";
}

export function formatMLBLocalDate(localDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(localDate || ""))) return "";
  const [year, month, day] = String(localDate).split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" })
    .format(new Date(year, month - 1, day, 12));
}

const fixtureStart = (row) => clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.start_timestamp || row?.start_time_utc);

export function canonicalFixtureStartMetadata(rows) {
  const candidates = (Array.isArray(rows) ? rows : []).map((row) => ({ row, value: fixtureStart(row) })).filter(({ value }) => value);
  const values = [...new Set(candidates.map(({ value }) => value))];
  if (!values.length) return { status: "UNAVAILABLE", value: null, representativeRow: null };
  if (values.length > 1) return { status: "CONFLICT", value: null, representativeRow: null, values };
  return { status: "AVAILABLE", value: values[0], representativeRow: candidates[0].row };
}

export function gameOptionsFromRows(rows) {
  return Array.from(new Set((Array.isArray(rows) ? rows : [])
    .filter((row) => !String(row?.Player || "").includes(" + "))
    .map(singleSportGameKey)
    .filter(Boolean))).sort();
}

export function buildAwayAtHomeGameOptions(rows) {
  const fixtures = new Map();
  const orientedByGameId = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const sport = clean(row?.Sport || row?.League).toUpperCase();
    if (!["NFL", "CFB", "MLB", "WNBA"].includes(sport)) continue;
    const key = canonicalTeamSportFixtureKey(row);
    if (!key) continue;
    const team = clean(row?.Team || row?.["Team Name"]);
    const opponent = clean(row?.Opponent || row?.["Opponent Name"]);
    const sourceHome = clean(row?.["Home Team"] || row?.HomeTeam || row?.home_team);
    const sourceAway = clean(row?.["Away Team"] || row?.AwayTeam || row?.away_team);
    const homeAway = clean(row?.["Home/Away"] || row?.HomeAway || row?.homeAway || row?.home_away).toUpperCase();
    const gameId = clean(row?.["Game ID"] || row?.GameID || row?.game_id);
    const current = fixtures.get(key) || {
      key,
      gameIds: new Set(),
      homeNames: new Set(),
      awayNames: new Set(),
      representativeRow: null,
      rows: [],
    };
    current.rows.push(row);
    if (sourceHome) current.homeNames.add(sourceHome);
    if (sourceAway) current.awayNames.add(sourceAway);
    if (homeAway === "HOME") {
      current.homeNames.add(team);
      current.awayNames.add(opponent);
      current.representativeRow ||= row;
    } else if (homeAway === "AWAY") {
      current.homeNames.add(opponent);
      current.awayNames.add(team);
      current.representativeRow ||= row;
    }
    if (gameId) current.gameIds.add(gameId);
    if (gameId && ["HOME", "AWAY"].includes(homeAway)) {
      const oriented = orientedByGameId.get(gameId) || { homeNames: new Set(), awayNames: new Set(), representativeRow: row };
      oriented.homeNames.add(homeAway === "HOME" ? team : opponent);
      oriented.awayNames.add(homeAway === "AWAY" ? team : opponent);
      orientedByGameId.set(gameId, oriented);
    }
    fixtures.set(key, current);
  }
  return [...fixtures.values()].map((fixture) => {
    const linked = [...fixture.gameIds].map((id) => orientedByGameId.get(id)).find(Boolean);
    const home = preferredName(fixture.homeNames) || preferredName(linked?.homeNames || []);
    const away = preferredName(fixture.awayNames) || preferredName(linked?.awayNames || []);
    const start = canonicalFixtureStartMetadata(fixture.rows);
    return {
      key: fixture.key,
      display: home && away ? `${away} @ ${home}` : fixture.key,
      representativeRow: start.status === "CONFLICT" ? null : (start.representativeRow || fixture.representativeRow || linked?.representativeRow || null),
      startTimeStatus: start.status,
      canonicalStartTime: start.value,
      startTimeConflicts: start.values || [],
    };
  }).sort((a, b) => a.display.localeCompare(b.display));
}

export const SOCCER_COMPETITION_ORDER = [
  "Premier League", "La Liga", "Serie A", "Bundesliga", "Ligue 1", "MLS", "Other / Unsupported",
];

const clean = (value) => String(value ?? "").trim();
const normalized = (value) => clean(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export function soccerCompetition(row) {
  const raw = clean(row?.Competition || row?.League || "Other / Unsupported");
  return SOCCER_COMPETITION_ORDER.includes(raw) ? raw : "Other / Unsupported";
}

function teamSides(row) {
  const team = clean(row?.Team || row?.["Team Name"]);
  const opponent = clean(row?.Opponent || row?.["Opponent Name"]);
  const sourceHome = clean(row?.["Home Team"] || row?.HomeTeam || row?.home_team);
  const sourceAway = clean(row?.["Away Team"] || row?.AwayTeam || row?.away_team);
  if (sourceHome && sourceAway) return { home: sourceHome, away: sourceAway };
  const away = /^away$/i.test(clean(row?.["Home/Away"]));
  return away ? { home: opponent, away: team } : { home: team, away: opponent };
}

export function canonicalSoccerFixtureKey(row) {
  const id = clean(row?.["Game ID"] || row?.["Fixture ID"] || row?.GameID || row?.FixtureID);
  if (id) return `soccer:id:${id}`;
  const competition = normalized(soccerCompetition(row));
  const { home, away } = teamSides(row);
  const kickoff = clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.start_timestamp);
  if (!competition || !home || !away || !kickoff) return "";
  return `soccer:fixture:${competition}|${normalized(home)}|${normalized(away)}|${kickoff}`;
}

function preferredName(names) {
  return [...names].filter(Boolean).sort((a, b) => b.length - a.length || a.localeCompare(b))[0] || "";
}

function sameTeamAlias(left, right) {
  const a = normalized(left);
  const b = normalized(right);
  if (!a || !b) return false;
  if (a === b) return true;
  const [shorter, longer] = [a, b].sort((x, y) => x.length - y.length);
  return shorter.length >= 5 && (longer.startsWith(shorter) || longer.endsWith(shorter));
}

export function buildSoccerFixtureGroups(rows) {
  const fixtures = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (String(row?.Sport || "SOCCER").toUpperCase() !== "SOCCER") continue;
    const key = canonicalSoccerFixtureKey(row);
    if (!key) continue;
    const { home, away } = teamSides(row);
    if (!home || !away) continue;
    const hasStableId = key.startsWith("soccer:id:");
    const kickoff = clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.start_timestamp);
    const equivalent = hasStableId ? null : [...fixtures.values()].find((fixture) => (
      !fixture.hasStableId
      && fixture.competition === soccerCompetition(row)
      && fixture.kickoff === kickoff
      && sameTeamAlias(preferredName(fixture.homeNames), home)
      && sameTeamAlias(preferredName(fixture.awayNames), away)
    ));
    const fixtureKey = equivalent?.key || key;
    const current = equivalent || fixtures.get(fixtureKey) || {
      key: fixtureKey,
      hasStableId,
      competition: soccerCompetition(row),
      kickoff,
      homeNames: new Set(),
      awayNames: new Set(),
      sourceKeys: new Set(),
      rows: [],
      representativeRow: row,
    };
    current.homeNames.add(home);
    current.awayNames.add(away);
    current.sourceKeys.add(key);
    current.rows.push(row);
    if (!current.kickoff) current.kickoff = clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.start_timestamp);
    fixtures.set(fixtureKey, current);
  }

  const byCompetition = new Map(SOCCER_COMPETITION_ORDER.map((name) => [name, []]));
  for (const fixture of fixtures.values()) {
    const home = preferredName(fixture.homeNames);
    const away = preferredName(fixture.awayNames);
    byCompetition.get(fixture.competition).push({
      key: fixture.key,
      competition: fixture.competition,
      kickoff: fixture.kickoff,
      // Soccer display follows canonical home/away identity; alphabetical
      // sorting silently reversed verified fixtures such as Villa–Arsenal.
      display: `${away} @ ${home}`,
      sourceKeys: [...fixture.sourceKeys],
      rowCount: fixture.rows.length,
      representativeRow: fixture.representativeRow,
    });
  }
  return SOCCER_COMPETITION_ORDER.map((competition) => ({
    competition,
    games: byCompetition.get(competition).sort((a, b) => {
      const aTime = Date.parse(a.kickoff);
      const bTime = Date.parse(b.kickoff);
      return (Number.isFinite(aTime) ? aTime : Number.MAX_SAFE_INTEGER) - (Number.isFinite(bTime) ? bTime : Number.MAX_SAFE_INTEGER)
        || a.display.localeCompare(b.display);
    }),
  })).filter((group) => group.games.length);
}

export function soccerFixtureSelectionKey(row, groups) {
  const sourceKey = canonicalSoccerFixtureKey(row);
  for (const group of Array.isArray(groups) ? groups : []) {
    const game = group.games.find((candidate) => candidate.sourceKeys.includes(sourceKey));
    if (game) return game.key;
  }
  return sourceKey;
}

export function canonicalNFLFixtureKey(row) {
  const id = clean(row?.["Game ID"] || row?.GameID || row?.game_id);
  if (id) return `nfl:id:${id}`;
  const team = clean(row?.Team || row?.["Team Name"]);
  const opponent = clean(row?.Opponent || row?.["Opponent Name"]);
  const homeAway = clean(row?.["Home/Away"]).toUpperCase();
  const sourceHome = clean(row?.["Home Team"] || row?.HomeTeam || row?.home_team);
  const sourceAway = clean(row?.["Away Team"] || row?.AwayTeam || row?.away_team);
  const { home, away } = teamSides(row);
  const kickoff = clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.["Start Time"] || row?.start_time);
  if (!home || !away || !kickoff) return "";
  if (!sourceHome || !sourceAway) {
    if (["HOME", "AWAY"].includes(homeAway)) {
      return `nfl:fixture:${normalized(away)}|${normalized(home)}|${kickoff}`;
    }
    const pair = [normalized(team), normalized(opponent)].sort();
    return `nfl:fixture:unknown|${pair[0]}|${pair[1]}|${kickoff}`;
  }
  return `nfl:fixture:${normalized(away)}|${normalized(home)}|${kickoff}`;
}

export function buildNFLFixtureOptions(rows) {
  const fixtures = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (String(row?.Sport || "NFL").toUpperCase() !== "NFL") continue;
    const key = canonicalNFLFixtureKey(row);
    if (!key) continue;
    const team = clean(row?.Team || row?.["Team Name"]);
    const opponent = clean(row?.Opponent || row?.["Opponent Name"]);
    const homeAway = clean(row?.["Home/Away"] || row?.HomeAway || row?.home_away).toUpperCase();
    const sourceHome = clean(row?.["Home Team"] || row?.HomeTeam || row?.home_team);
    const sourceAway = clean(row?.["Away Team"] || row?.AwayTeam || row?.away_team);
    const { home, away } = teamSides(row);
    if (!team || !opponent) continue;
    const current = fixtures.get(key) || {
      key,
      homeNames: new Set(),
      awayNames: new Set(),
      unknownPairs: [],
      kickoff: clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.["Start Time"] || row?.start_time),
      representativeRow: row,
      rowCount: 0,
    };
    if (sourceHome && sourceAway) {
      current.homeNames.add(sourceHome);
      current.awayNames.add(sourceAway);
    } else if (["HOME", "AWAY"].includes(homeAway)) {
      current.homeNames.add(home);
      current.awayNames.add(away);
    } else {
      current.unknownPairs.push([team, opponent]);
    }
    current.rowCount += 1;
    fixtures.set(key, current);
  }
  return [...fixtures.values()].map((fixture) => {
    let home = preferredName(fixture.homeNames);
    let away = preferredName(fixture.awayNames);
    if (!home || !away) {
      const fallback = fixture.unknownPairs[0] || ["", ""];
      [home, away] = fallback;
    }
    return {
      key: fixture.key,
      display: `${away} @ ${home}`,
      kickoff: fixture.kickoff,
      representativeRow: fixture.representativeRow,
      rowCount: fixture.rowCount,
    };
  }).sort((a, b) => {
    const aTime = Date.parse(a.kickoff);
    const bTime = Date.parse(b.kickoff);
    return (Number.isFinite(aTime) ? aTime : Number.MAX_SAFE_INTEGER) - (Number.isFinite(bTime) ? bTime : Number.MAX_SAFE_INTEGER)
      || a.display.localeCompare(b.display);
  });
}

export const NFL_WINDOW_ORDER = [
  "Thursday Night", "Thanksgiving", "Christmas", "Saturday", "International",
  "Sunday Early", "Sunday Afternoon", "Sunday Night", "Monday Night", "Other",
];

function localizedKickoffParts(kickoff, timeZone) {
  const date = new Date(kickoff);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(values.year),
    weekday: values.weekday,
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
  };
}

function localHourFor(date, timeZone) {
  const parts = localizedKickoffParts(date, timeZone);
  return parts ? parts.hour + parts.minute / 60 : Number.NaN;
}

export function nflKickoffWindow(option, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  const parts = localizedKickoffParts(option?.kickoff, timeZone);
  if (!parts) return "Other";
  const row = option?.representativeRow || {};
  if (row.International === true || /international/i.test(clean(row?.["Game Window"] || row?.GameWindow || row?.event_type))) return "International";
  if (parts.month === 12 && parts.day === 25) return "Christmas";
  if (parts.weekday === "Thu" && parts.month === 11 && parts.day >= 22 && parts.day <= 28) return "Thanksgiving";
  if (parts.weekday === "Thu") return "Thursday Night";
  if (parts.weekday === "Mon") return "Monday Night";
  if (parts.weekday === "Sat") return "Saturday";
  if (parts.weekday !== "Sun") return "Other";

  // NFL national windows are anchored to Eastern time. Translate the
  // standard 3:30/6:30 PM ET boundaries into the viewer's local clock so
  // 1 PM ET and 4:05/4:25 PM ET cohorts stay together in every US zone.
  const date = new Date(option.kickoff);
  const localHour = parts.hour + parts.minute / 60;
  const easternHour = localHourFor(date, "America/New_York");
  const localOffsetFromEastern = localHour - easternHour;
  const internationalCutoff = 11 + localOffsetFromEastern;
  const earlyCutoff = 15.5 + localOffsetFromEastern;
  const nightCutoff = 18.5 + localOffsetFromEastern;
  if (localHour < internationalCutoff) return "International";
  if (localHour < earlyCutoff) return "Sunday Early";
  if (localHour < nightCutoff) return "Sunday Afternoon";
  return "Sunday Night";
}

export function groupNFLFixtureOptions(options, timeZone) {
  const zone = timeZone || "America/Chicago";
  const groups = new Map();
  for (const option of Array.isArray(options) ? options : []) {
    const parts = localizedKickoffParts(option?.kickoff, zone);
    const window = nflKickoffWindow(option, zone);
    const date = parts ? `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}` : "Date unavailable";
    const key = `${date}|${window}`;
    if (!groups.has(key)) groups.set(key, { key, date, window, games: [] });
    groups.get(key).games.push(option);
  }
  return [...groups.values()].map((group) => ({
    ...group,
    label: `${group.date} · ${group.window}`,
    games: group.games.sort((a, b) => Date.parse(a.kickoff) - Date.parse(b.kickoff) || a.display.localeCompare(b.display)),
  })).sort((a, b) => {
    const timeA = Date.parse(a.games[0]?.kickoff);
    const timeB = Date.parse(b.games[0]?.kickoff);
    return (Number.isFinite(timeA) ? timeA : Number.MAX_SAFE_INTEGER) - (Number.isFinite(timeB) ? timeB : Number.MAX_SAFE_INTEGER)
      || NFL_WINDOW_ORDER.indexOf(a.window) - NFL_WINDOW_ORDER.indexOf(b.window);
  });
}

export function canonicalCFBFixtureKey(row) {
  const id = clean(row?.["Game ID"] || row?.GameID || row?.game_id);
  if (id) return `cfb:id:${id}`;
  const { home, away } = teamSides(row);
  const kickoff = clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.["Start Time"]);
  return home && away && kickoff ? `cfb:fixture:${normalized(home)}|${normalized(away)}|${kickoff}` : "";
}

export function cfbKickoffWindow(option, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  const parts = localizedKickoffParts(option?.kickoff, timeZone);
  if (!parts) return "Sunday / Other";
  if (parts.weekday === "Thu") return "Thursday";
  if (parts.weekday === "Fri") return "Friday";
  if (parts.weekday !== "Sat") return "Sunday / Other";
  const hour = parts.hour + parts.minute / 60;
  if (hour < 14) return "Saturday Early";
  if (hour < 18) return "Saturday Afternoon";
  if (hour < 21.5) return "Saturday Primetime";
  return "Saturday Late";
}

export function buildCFBFixtureGroups(rows, timeZone) {
  const fixtures = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (clean(row?.Sport).toUpperCase() !== "CFB") continue;
    const key = canonicalCFBFixtureKey(row);
    if (!key) continue;
    const { home, away } = teamSides(row);
    if (!home || !away) continue;
    const kickoff = clean(row?.["Game Start Timestamp"] || row?.GameStartTimestamp || row?.["Start Time"]);
    const conference = clean(row?.Conference) || "Unresolved / Other";
    const current = fixtures.get(key) || { key, home, away, kickoff, conference, representativeRow: row, rowCount: 0 };
    current.rowCount += 1;
    fixtures.set(key, current);
  }
  const grouped = new Map();
  for (const fixture of fixtures.values()) {
    const window = cfbKickoffWindow(fixture, timeZone);
    const conference = fixture.conference || "Unresolved / Other";
    if (!grouped.has(conference)) grouped.set(conference, new Map());
    if (!grouped.get(conference).has(window)) grouped.get(conference).set(window, []);
    grouped.get(conference).get(window).push({ ...fixture, display: `${fixture.away} @ ${fixture.home}` });
  }
  return [...grouped.entries()].sort(([a], [b]) => a === "Unresolved / Other" ? 1 : b === "Unresolved / Other" ? -1 : a.localeCompare(b)).map(([conference, windows]) => ({
    conference,
    windows: [...windows.entries()].map(([window, games]) => ({
      window,
      games: games.sort((a, b) => Date.parse(a.kickoff) - Date.parse(b.kickoff) || a.display.localeCompare(b.display)),
    })),
  }));
}
